/**
 * core の盤面をフレームごとに引く（Issue #348）。
 *
 * サービスは MSG ごとの盤面の変化を `board_steps`（ocgcore/service/board_steps.py）で返す。
 * 各ステップの index は events の index と同じなので、resolveEventTimes の rows（イベントの時刻）で ms を付け、
 * 時刻順に畳んだ盤面を 1 ステップ 1 つ持つ（変わらないゾーン・カードは前のものを共有するので軽い）。
 *
 * 盤面の形（boardAt の戻り値）:
 *   { players: [{ hand: [uid], grave: [uid|null], removed: [uid], mzone: [uid|null ×7], szone: [uid|null ×8], deck: n, extra: n }, {...}],
 *     cards: { uid: { code, pos } }, stats: { uid: { atk, def, level } } }
 * - links は場のカードの装備先・対象・カウンター（Issue #438 / #439。uid → { equipped_to, targets, counters }。場を離れたら消える）
 * - stats は表側のモンスターの今の ATK / DEF / レベル（Issue #379。core は MSG に出さないので再実行が区切りごとに query した値）
 * - スナップショットの statChanges は、場に居続けたモンスターのステータスが変わった瞬間（effects/stat.js が演出にする）
 * - mzone の 0〜4 がメインモンスターゾーン、szone の 0〜4 が魔法罠ゾーン・5 がフィールド魔法（core の並び）
 * - pos は core の POS_*（1 表攻撃 / 2 裏攻撃 / 4 表守備 / 8 裏守備）。場のカードだけ意味がある
 * - デッキ・EX デッキは枚数だけ（中身はアノテーションで決まるまで分からない）
 * **時刻は引数の frameMs だけ**（書き出しで同じ絵にするため。purity.test.js）。
 */
import { lastIndexAtOrBefore } from './hudState';

// 表示形式の読み方は `ocgPosition.js`（描く層の入口も読むので台本の外に置いた。Issue #766）
export { POS, isFaceUp, isDefense } from './ocgPosition';

const ZONES = ['hand', 'grave', 'removed', 'mzone', 'szone'];
const EMPTY_PLAYER = { hand: [], grave: [], removed: [], mzone: Array(7).fill(null), szone: Array(8).fill(null), deck: 0, extra: 0 };

function initialState(boardSteps) {
  const init = boardSteps?.initial ?? {};
  const players = [0, 1].map((p) => {
    const z = init.zones?.[String(p)] ?? {};
    const out = { ...EMPTY_PLAYER };
    ZONES.forEach((name) => { if (Array.isArray(z[name])) out[name] = z[name]; });
    out.deck = init.counts?.deck?.[p] ?? 0;
    out.extra = init.counts?.extra?.[p] ?? 0;
    return out;
  });
  return { players, cards: {}, stats: {}, links: {} };
}

/** 1 ステップを畳んだ新しい盤面（変わらない部分は前の参照のまま）。 */
export function applyStep(state, step) {
  let players = state.players;
  const touch = (p) => {
    if (players === state.players) players = [...state.players];
    if (players[p] === state.players[p]) players[p] = { ...state.players[p] };
    return players[p];
  };
  Object.entries(step.zones ?? {}).forEach(([p, zs]) => {
    const pl = touch(Number(p));
    Object.entries(zs).forEach(([name, list]) => { pl[name] = list; });
  });
  Object.entries(step.counts ?? {}).forEach(([key, values]) => {
    values.forEach((n, p) => { if (players[p][key] !== n) touch(p)[key] = n; });
  });
  const cards = step.cards ? { ...state.cards, ...step.cards } : state.cards;
  const stats = step.stats ? { ...(state.stats ?? {}), ...step.stats } : (state.stats ?? {});
  let links = state.links ?? {};
  if (step.links) {
    links = { ...links };
    Object.entries(step.links).forEach(([uid, v]) => { if (v == null) delete links[uid]; else links[uid] = v; });
  }
  return players === state.players && cards === state.cards && stats === state.stats && links === state.links ? state : { players, cards, stats, links };
}

/**
 * @param boardSteps OcgGetState の board_steps（無ければ空の盤面だけ）
 * @param rows resolveEventTimes().rows
 */
export function buildBoardTimeline(boardSteps, rows = []) {
  const msByIndex = new Map(rows.map((r) => [r.event.index, r.ms]));
  const initial = initialState(boardSteps);
  const snapshots = [];
  let state = initial;
  let lastMs = 0;
  for (const step of boardSteps?.steps ?? []) {
    const ms = msByIndex.has(step.index) ? Math.max(lastMs, msByIndex.get(step.index)) : lastMs;
    state = applyStep(state, step);
    snapshots.push({ ms, index: step.index, state, ...(step.stat_changes?.length ? { statChanges: step.stat_changes } : {}) });
    lastMs = ms;
  }
  return { initial, snapshots };
}

/** frameMs 時点の盤面。同じ時刻に複数のステップがあれば最後のもの。 */
export function boardAt(frameMs, timeline) {
  if (!timeline) return initialState(null);
  const i = lastIndexAtOrBefore(timeline.snapshots, frameMs);
  return i >= 0 ? timeline.snapshots[i].state : timeline.initial;
}

/**
 * イベント index の MSG を畳み終えた直後の盤面（時刻ではなく並びで引く。Issue #372）。
 * 演出の帯は元のイベントより前後にずらせるので、フレームの時刻で盤面を引くと「まだカードが置かれていない」盤面になる。
 * 演出（layer: 'event'）はこれで開いたイベントの直後の盤面を使う。
 */
export function boardAfterIndex(index, timeline) {
  if (!timeline) return initialState(null);
  const snaps = timeline.snapshots;
  let lo = 0; let hi = snaps.length - 1; let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (snaps[mid].index <= index) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found >= 0 ? snaps[found].state : timeline.initial;
}

/** 盤面のカードを並べる（演出が「どのカードがどこにあるか」を引く用）。 */
export function boardCards(board) {
  const out = [];
  board.players.forEach((pl, player) => {
    ZONES.forEach((zone) => {
      pl[zone].forEach((uid, seq) => {
        if (!uid) return;
        const c = board.cards[uid] ?? {};
        out.push({ uid, code: c.code ?? 0, pos: c.pos ?? 0, player, zone, seq });
      });
    });
  });
  return out;
}

/** uid のカードが今どこにあるか（{ uid, code, pos, player, zone, seq }。盤面に居なければ null）。演出が「そのカードの位置」を引く用 */
export function findCard(board, uid) {
  if (!uid || !board) return null;
  for (let player = 0; player < board.players.length; player += 1) {
    const pl = board.players[player];
    for (const zone of ZONES) {
      const seq = pl[zone].indexOf(uid);
      if (seq >= 0) {
        const c = board.cards[uid] ?? {};
        return { uid, code: c.code ?? 0, pos: c.pos ?? 0, player, zone, seq, owner: c.owner ?? null };
      }
    }
  }
  return null;
}
